import { Ban, MoreVertical, Pencil, Power, Trash2 } from "lucide-react";

import { ApiKeysService, type ApiKeyDetail } from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ApiKeyActionsMenuProps {
  apiKey: ApiKeyDetail;
  onEdit: (key: ApiKeyDetail) => void;
  onDisable: (key: ApiKeyDetail) => void;
  onRevoke: (key: ApiKeyDetail) => void;
  /** Called after a successful Enable mutation so the list can refetch. */
  onEnableSuccess: () => void;
}

export function ApiKeyActionsMenu({
  apiKey,
  onEdit,
  onDisable,
  onRevoke,
  onEnableSuccess,
}: ApiKeyActionsMenuProps) {
  const { t } = useTranslation();
  const enable = useMutation<void, ApiKeyDetail>(
    () => ApiKeysService.enable(apiKey.id),
    { onSuccess: onEnableSuccess }
  );

  const isRevoked = apiKey.status === "revoked";
  const isDisabled = apiKey.status === "disabled";
  const isActive = apiKey.status === "active";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t("apiKeys.actions.rowAriaLabel", { name: apiKey.name })}
      >
        <MoreVertical size={16} />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem
          onClick={() => onEdit(apiKey)}
          disabled={isRevoked}
        >
          <Pencil size={14} />
          {t("apiKeys.actions.edit")}
        </DropdownMenuItem>

        {isDisabled && (
          <DropdownMenuItem
            onClick={() => void enable.mutate()}
            disabled={enable.isLoading}
          >
            <Power size={14} />
            {enable.isLoading
              ? t("apiKeys.actions.enabling")
              : t("apiKeys.actions.enable")}
          </DropdownMenuItem>
        )}

        {isActive && (
          <DropdownMenuItem onClick={() => onDisable(apiKey)}>
            <Ban size={14} />
            {t("apiKeys.actions.disable")}
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          variant="danger"
          onClick={() => onRevoke(apiKey)}
          disabled={isRevoked}
        >
          <Trash2 size={14} />
          {t("apiKeys.actions.revoke")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
